import { Component, OnDestroy, OnInit } from '@angular/core';
import { IWindow } from '@phdsoft/window';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { DigitalTwinService } from 'src/app/digital-twin/digital-twin.service';
import { AssetInfoViewService } from 'src/app/template/asset-info-view/asset-info-view/asset-info-view.service';
import { CriticalAreaService, ICriticalAreaDTO, ICriticalAreaElementDTO, RegionsAndSectionsRisk } from 'src/app/template/critical-area-window/critical-area.service';
import { RiskLevel } from '../risk-shield/risk-shield-config.model';
import { Region } from 'src/app/platform/busines-data/Framework/Region';
import { IReasons, ProbabilityOfFailure } from 'src/app/template/critical-area-window/critical-area.model';
import { ChangesModel } from 'projects/phdsoft/common/src/lib/models/changes.model';
import { AssetManagerLoaderService } from 'src/app/asset-manager/asset-manager-loader.service';
import { RegionsService } from 'src/app/admin/services/regions.service';
import { ThreedService } from 'src/app/shared/services/threed.service';
import { RegionItem } from '@phdsoft/common';
import { SectionItem } from 'projects/phdsoft/common/src/lib/models/sectionitem-info.model';
import { Section } from 'src/app/platform/busines-data/Framework/Section';
import POFCategorization from 'src/app/template/critical-area-window/POFCategorization';
import { AssetManagerFilter } from 'src/app/asset-manager/asset-manager-filter';
import { RiskMatrixService } from 'src/app/admin/pages/risk-setup/service/risk-matrix.service';
import { ConsequenceItem, ProbabilityOfFailureItem, RiskLevelItem, RiskMatrix } from 'src/app/admin/pages/risk-setup/types/risk-config.model';
import { DTSelectionService } from 'src/app/digital-twin/selection/selection.service';
import { ISideboard } from 'src/app/side-board/ISideboard';
import { TemplateService } from '@app/template/template.service';
import { MenuService, VisualizationMode } from '@app/template/menu/menu.service';
import { Subscription } from 'rxjs';
interface ConsolidatedRegionRisk {
  name: string
  id: string
  sections: ConsolidatedSectionRisk[]
  riskLevelId: string
  riskLevelColor: string
  highestReason?: IReasons
  overallRiskScore?: number
  expanded: boolean
}

interface ConsolidatedSectionRisk {
  name: string
  id: string
  criticalAreas: ConsolidatedCriticalAreaRisk[]
  riskLevelId: string
  riskLevelColor: string
  highestReason?: IReasons
  overallRiskScore?: number
  expanded: boolean
}

interface ConsolidatedCriticalAreaRisk {
  id: string
  name: string
  highestReason?: IReasons
  riskLevelId: string
  riskLevelColor: string
  riskScore?: number
  expanded: boolean
}

export interface MatrixCell {
  count: number;
  regions: ConsolidatedRegionRisk[];
}

export interface MatrixData {
  cellContents: { [key: string]: MatrixCell };
  unratedRegions: ConsolidatedRegionRisk[];
}

@Component({
  selector: 'phdsoft-risk-assessment',
  templateUrl: './risk-assessment.component.html',
  styleUrls: ['./risk-assessment.component.scss']
})
export class RiskAssessmentComponent implements OnInit, OnDestroy {

  isLoading: boolean

  isLoadingStructure: boolean
  isLoadingComponentData: boolean
  footerColSpan: number

  unratedRegionsCount: number
  unratedRegionsIds: string[]

  currentRegionIds: string[]
  regions: RegionItem[]
  sections: SectionItem[]
  currentRegions: RegionItem[]
  assetId: string
  customerId: string

  riskMatrix: RiskMatrix
  matrixData: MatrixData;
  regionsAndSectionsRisk: RegionsAndSectionsRisk;
  cellContents: { [key: string]: number } = {};
  consolidatedRegionsRisk: ConsolidatedRegionRisk[]
  consolidatedRegionsRiskFiltered: ConsolidatedRegionRisk[]
  private riskLevelColorMap: {[key: string]: string} = {};
  selectedCell: { consequenceId: string; probabilityOfFailureId: string } | null = null;
  private previousVisualizationMode: VisualizationMode;
  private forcedRiskVisualizationMode = false;
  private criticalAreasChangedSubscription: Subscription;
  private riskRefreshSeq = 0;


  constructor(
    private assetManager: AssetManager,
    private criticalAreaService: CriticalAreaService,
    private assetInfoViewService: AssetInfoViewService,
    private assetManagerLoaderService: AssetManagerLoaderService,
    private threeDService: ThreedService,
    private pofCategorization: POFCategorization,
    private assetManagerFilter: AssetManagerFilter,
    private riskMatrixService: RiskMatrixService,
    private selectionService: DTSelectionService,
    private templateService: TemplateService,
    private menuService: MenuService,
  ) { 
    this.assetId = this.assetManager.asset.assetInfo.AssetId
    this.customerId = this.assetManager.asset.assetInfo.CustomerId
    this.assetInfoViewService.castCurrentRegionIds.subscribe(async (data) => {
      this.currentRegionIds = data
    })
  }

  ngOnDestroy(): void {
    this.criticalAreasChangedSubscription?.unsubscribe();
    if (this.forcedRiskVisualizationMode) {
      this.menuService.changeVisualizationMode(this.previousVisualizationMode, false);
    }
  }

  closeWindow() {
    this.templateService.showRiskAssessment = false
  }

  optionsWindow: ISideboard = {
    VisibleMini: true,
    VisibleClose: true,
    //isMinimized: this.isMinimizePW,
    Header: {
      VisibleTitle: true,
      Title: 'Risk Assessment',
    },
    Footer: {
      Visible: true,
      ShowCancel: false,
      ShowOk: false
    },
  }

  async ngOnInit() {
    this.isLoadingComponentData = true;
    this.previousVisualizationMode = this.menuService.visualizationMode;
    this.forcedRiskVisualizationMode = this.previousVisualizationMode !== 'Risk';
    await this.initializeData();
    this.menuService.changeVisualizationMode('Risk' as VisualizationMode, false);
    this.isLoadingComponentData = false;
    // The window stays open while the user edits critical areas in a section:
    // refetch the matrix whenever one is saved or deleted on the backend.
    this.criticalAreasChangedSubscription = this.criticalAreaService.criticalAreasChanged$
      .subscribe(() => this.refreshRegionsAndSectionsRisk());
  }

  private processInitialData() {
    this.assetManagerFilter.showAllCriticalAreas(false);
    this.buildRiskViews();
  }

  private buildRiskViews() {
    this.consolidatedRegionsRisk =  this.createConsolidatedRegionRisks(this.regions, this.sections, this.regionsAndSectionsRisk)
    this.consolidatedRegionsRiskFiltered = this.filterConsolidatedRegionsRisk(this.consolidatedRegionsRisk)
    this.matrixData = this.populateMatrixData(this.consolidatedRegionsRisk);
  }

  /** Refetch the risk after a critical area change; a later refresh supersedes an earlier one still in flight. */
  private async refreshRegionsAndSectionsRisk() {
    const seq = ++this.riskRefreshSeq;
    try {
      const risk = await this.criticalAreaService.getRegionsAndSectionsRisk(null, this.assetId).toPromise();
      if (seq !== this.riskRefreshSeq || !risk) {
        return;
      }
      this.regionsAndSectionsRisk = risk;
      this.buildRiskViews();
    } catch (error) {
      console.error('Error refreshing regions and sections risk:', error);
    }
  }

  filterConsolidatedRegionsRisk = (regions: ConsolidatedRegionRisk[]): ConsolidatedRegionRisk[] => {
    return regions
      .filter(region => region.riskLevelId !== '')
      .map(region => ({
        ...region,
        sections: region.sections
          .filter(section => section.riskLevelId !== '')
          .map(section => ({
            ...section,
            criticalAreas: section.criticalAreas.filter(
              area => area.riskLevelId !== ''
            )
          }))
          .filter(section => section.criticalAreas.length > 0)
      }))
      .filter(region => region.sections.length > 0);
  };

  private async initializeData() {
    await Promise.all([
      this.loadAllRegions(),
      this.fetchRiskMatrixData(),
      this.fetchRegionsAndSectionsRisk()
    ]);

    this.processInitialData();
  }

   private async loadAllRegions() {
    [this.regions, this.sections] = await this.getAllRegionsAndSections();
    this.currentRegions = this.regions.filter(x => this.currentRegionIds.includes(x.id));
  }

  private async fetchRiskMatrixData() {
    return new Promise<void>((resolve, reject) => {
      this.riskMatrixService.fetchRiskMatrixData(this.customerId).subscribe({
        next: ({ riskMatrixData, riskLevelItems, probabilityOfFailureItems, consequenceItems }) => {
          this.handleRiskMatrixData(riskMatrixData);
          this.handleRiskLevelItems(riskLevelItems);
          this.handleProbabilityOfFailureItems(probabilityOfFailureItems);
          this.handleConsequenceItems(consequenceItems);
          resolve();
        },
        error: error => {
          console.error('Error fetching risk matrix data:', error);
          reject(error);
        }
      });
    });
  }

  private fetchRegionsAndSectionsRisk() {
    return new Promise<void>((resolve, reject) => {
      if (!this.assetId) {
        console.error("No assetId provided");
        reject("No assetId provided");
        return;
      }

      this.criticalAreaService.getRegionsAndSectionsRisk(null, this.assetId).subscribe({
        next: (x) => {
          this.regionsAndSectionsRisk = x;
          resolve();
        },
        error: error => {
          console.error('Error fetching regions and sections risk:', error);
          reject(error);
        }
      });
    });
  }


  async loadCriticalAreaByRegionIds(regionIds: string[]){
    let criticalAreas = [];
    let criticalAreaElements = [];

    for (let regionId of regionIds) {
        let result = await this.criticalAreaService.getCriticalAreas(regionId, this.assetId).toPromise();

        if(!result || !result.criticalAreaElements || !result.criticalAreas){
          continue;
        }
        criticalAreas = criticalAreas.concat(result.criticalAreas);
        criticalAreaElements = criticalAreaElements.concat(result.criticalAreaElements);
    }

    return { criticalAreas: criticalAreas, criticalAreaElements: criticalAreaElements };
  }

  handleRiskMatrixData(riskMatrix: RiskMatrix) {
    if(!riskMatrix){
      return
    }

    this.riskMatrix = riskMatrix
  }

  private handleProbabilityOfFailureItems(probabilityOfFailureItems: ProbabilityOfFailureItem[]) {
    if(!probabilityOfFailureItems) {
      return
    }

    this.riskMatrix.probabilityOfFailureItems = probabilityOfFailureItems
  }

  private handleConsequenceItems(consequenceItems: ConsequenceItem[]) {
    if(!consequenceItems) {
      return
    }

    this.riskMatrix.consequenceItems = consequenceItems
  }

  private handleRiskLevelItems(riskLevels: any) {

    if (!riskLevels) {
      this.handleMissingRiskLevels();
      return;
    }

    riskLevels.forEach(rl => {
      this.riskLevelColorMap[rl.id] = rl.color;
    });

    this.riskMatrix.riskLevelItems = riskLevels;
  }

  handleMissingRiskLevels() {
    //show an ALERT
    // this.snackBarService.openSnackBar("No risk levels found, please add some.")
    //this.riskMatrix.riskLevelItems = []
  }

  populateMatrix() {
    for (const region of this.regionsAndSectionsRisk.regions) {
      if(region?.highestReason?.consequenceId == null || region?.highestReason?.probabilityOfFailureId == null){
        continue
      }
      const key = `${region.highestReason.consequenceId}-${region.highestReason.probabilityOfFailureId}`;
      this.cellContents[key] = (this.cellContents[key] || 0) + 1;
    }
    console.log("this.cellContents")
    console.log(this.cellContents)
  }

  populateMatrixData(regions: ConsolidatedRegionRisk[]): MatrixData {
    const matrixData: MatrixData = {
      cellContents: {},
      unratedRegions: []
    };

    for (const region of regions) {
      if (region.highestReason?.consequenceId && region.highestReason?.probabilityOfFailureId) {
        const key = `${region.highestReason.consequenceId}-${region.highestReason.probabilityOfFailureId}`;

        if (!matrixData.cellContents[key]) {
          matrixData.cellContents[key] = { count: 0, regions: [] };
        }

        matrixData.cellContents[key].count++;
        matrixData.cellContents[key].regions.push(region);
      } else {
        matrixData.unratedRegions.push(region);
      }
    }

    return matrixData;
  }

  getCellContent(consequenceId: string, probabilityOfFailureId: string): number {
    const key = `${consequenceId}-${probabilityOfFailureId}`;
    return this.matrixData.cellContents[key]?.count || 0;
  }

  getCellColor(consequenceId: string, probabilityOfFailureId: string): string {
    const cell = this.riskMatrix.cells.find(
      c => c.consequenceId === consequenceId && c.probabilityOfFailureId === probabilityOfFailureId
    );
    const riskLevel = this.riskMatrix.riskLevelItems.find(r => r.id === cell?.riskLevelId);
    return riskLevel?.color || 'white';
  }

  getTextColor(backgroundColor: string): string {
    // Simple function to determine if text should be black or white based on background color
    const rgb = parseInt(backgroundColor.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >>  8) & 0xff;
    const b = (rgb >>  0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma < 128 ? 'white' : 'black';
  }

  async loadAllCriticalAreas() {
    return await this.criticalAreaService.getCriticalAreasByAssetId(this.assetId).toPromise()
  }

  async getRegionsRisk(loadAll: boolean, regionIds: string[]) {
    if(loadAll){
      return await this.criticalAreaService.getRegionsAndSectionsByAssetIdRiskColor(this.assetId).toPromise()
    }
    if(!loadAll){
      return await this.criticalAreaService.getRegionsAndSectionsRiskColor(regionIds, this.assetId).toPromise()
    }
  }

  createConsolidatedRegionRisks(
    regions: RegionItem[],
    sections: SectionItem[],
    regionsAndSectionsRisk: RegionsAndSectionsRisk
  ): ConsolidatedRegionRisk[] {

    let consolidatedRegionRisks = regions.map(region => {
      let regionSections = sections.filter(section => section.regions.includes(region.id));

      let regionRisk = regionsAndSectionsRisk.regions.find(x => x.regionId == region.id);

      let consolidatedSectionRisks: ConsolidatedSectionRisk[] = regionSections.map(section => {
        let sectionRisk = regionRisk?.sections.find(x => x.sectionId == section.id);

        let consolidatedCriticalAreaRisks: ConsolidatedCriticalAreaRisk[] = [];
        if(sectionRisk && sectionRisk.riskLevelId !== "" && sectionRisk.criticalAreas && sectionRisk.criticalAreas.length > 0) {
          consolidatedCriticalAreaRisks = sectionRisk.criticalAreas.map((criticalArea, index) => {
            let highestReason = criticalArea.HighestReason;
            let riskScore = highestReason ? highestReason.consequence * highestReason.probabilityOfFailure : 0;
            return {
              id: criticalArea?.id || '',
              name: 'CA' + (index + 1),
              highestReason: highestReason,
              riskLevelId: criticalArea.riskLevelId,
              riskLevelColor: this.getRiskLevelColor(criticalArea.riskLevelId),
              riskScore: riskScore,
              expanded: false
            };
          });
        }

        let sectionHighestReason = sectionRisk?.highestReason;

        let sectionOverallRiskScore = sectionHighestReason ?
          sectionHighestReason.probabilityOfFailure * sectionHighestReason.consequence :
          0;

        let riskLevelId = sectionRisk?.riskLevelId || "";

        return {
          name: section.name,
          id: section.id,
          criticalAreas: consolidatedCriticalAreaRisks,
          riskLevelId: riskLevelId,
          riskLevelColor: this.getRiskLevelColor(riskLevelId),
          highestReason: sectionHighestReason,
          overallRiskScore: sectionOverallRiskScore,
          expanded: false
        };
      });

      consolidatedSectionRisks = consolidatedSectionRisks.sort((a, b) => {
        if (a.riskLevelId === "") return 1;
        if (b.riskLevelId === "") return -1;
        return (b.overallRiskScore || 0) - (a.overallRiskScore || 0);
      });

      let regionHighestReason = regionRisk?.highestReason;

      let regionOverallRiskScore = regionHighestReason ?
        regionHighestReason.probabilityOfFailure * regionHighestReason.consequence :
        0;

      let riskLevelId = regionRisk?.riskLevelId || "";

      return {
        name: region.name,
        id: region.id,
        sections: consolidatedSectionRisks,
        riskLevelId: riskLevelId,
        riskLevelColor: this.getRiskLevelColor(riskLevelId),
        highestReason: regionHighestReason,
        overallRiskScore: regionOverallRiskScore,
        expanded: false
      };
    });

    // Then use it in your sorting function
    consolidatedRegionRisks = consolidatedRegionRisks.sort((a, b) => {
      if (a.riskLevelId === "") return 1;
      if (b.riskLevelId === "") return -1;
      return this.getRiskLevelOrder(b.riskLevelId) - this.getRiskLevelOrder(a.riskLevelId);
    });

    return consolidatedRegionRisks;
  }

  // Add this method to your component
  getRiskLevelOrder(riskLevelId: string): number {
    const riskLevel = this.riskMatrix.riskLevelItems.find(rl => rl.id === riskLevelId);
    return riskLevel ? riskLevel.order : 0; // Return 0 for empty riskLevelId
  }

  getRiskLevelColor(riskLevelId: string): string {
    return this.riskLevelColorMap[riskLevelId] || '#ffffff';
  }

  isSelectedCell(consequenceId: string, probabilityOfFailureId: string): boolean {
    return this.selectedCell?.consequenceId === consequenceId &&
          this.selectedCell?.probabilityOfFailureId === probabilityOfFailureId;
  }

  onCellClick(consequenceId: string, probabilityOfFailureId: string): void {
    if(this.isLoadingStructure){
      return;
    }

    if (this.isSelectedCell(consequenceId, probabilityOfFailureId)) {
      this.resetSelection();
      return;
    }

    const key = `${consequenceId}-${probabilityOfFailureId}`;
    const cellRegions = this.matrixData.cellContents[key]?.regions || [];

    if (cellRegions.length === 0) {
      return;
    }

    this.selectedCell = { consequenceId, probabilityOfFailureId };
    this.consolidatedRegionsRiskFiltered = cellRegions;
  }

  onUnratedClick(): void {
    if(this.isLoadingStructure){
      return;
    }

    if (this.isUnratedSelected()) {
      this.resetSelection();
      return;
    }

    if (this.matrixData.unratedRegions.length === 0) {
      return;
    }

    this.selectedCell = null;
    this.consolidatedRegionsRiskFiltered = this.matrixData.unratedRegions;
  }

  private resetSelection(): void {
    this.selectedCell = null;
    this.consolidatedRegionsRiskFiltered = this.filterConsolidatedRegionsRisk(this.consolidatedRegionsRisk)
  }

  private isUnratedSelected(): boolean {
    return this.selectedCell === null && this.consolidatedRegionsRiskFiltered === this.matrixData.unratedRegions;
  }

  private async getAllRegionsAndSections(): Promise<[RegionItem[], SectionItem[]]> {
    const [regions, sections] = await Promise.all([
      this.threeDService.getRegions(this.assetId).toPromise(),
      this.threeDService.getSections(this.assetId).toPromise()
    ]);

    return [regions, sections];
  }

  handleClickRegion(region: ConsolidatedRegionRisk): void {
    if (!this.isLoadingStructure) {
      this.toggleRegionVisibility(region);
      this.onRegionSelect(region);
      this.logData(region);
    }
  }

  handleClickSection(region: ConsolidatedRegionRisk, section: ConsolidatedSectionRisk): void {
    if (!this.isLoadingStructure) {
      this.toggleSectionVisibility(region, section);
      this.onSectionSelect(region, section);
      this.logData(section)
    }
  }

  handleClickCriticalArea(section: ConsolidatedSectionRisk, criticalArea: ConsolidatedCriticalAreaRisk) {
    if(!this.isLoadingStructure) {
      this.toggleCriticalAreaVisibility(section, criticalArea)
      this.onCriticalAreaSelect(criticalArea)
      this.logData(criticalArea)
    }
  }

  toggleRegionVisibility(region: ConsolidatedRegionRisk) {
    region.expanded = !region.expanded;

    if (region.expanded) {
      this.consolidatedRegionsRisk.forEach(r => {
        if (r !== region) {
          r.expanded = false;
        }
        r.sections.forEach(s => s.expanded = false);
      });
    }
  }

  toggleSectionVisibility(region: ConsolidatedRegionRisk, section: ConsolidatedSectionRisk) {
    section.expanded = !section.expanded;

    if (section.expanded) {
      region.sections.forEach(s => {
        if (s !== section) {
          s.expanded = false;
        }
      });
    }
  }

  toggleCriticalAreaVisibility(section: ConsolidatedSectionRisk, criticalArea: ConsolidatedCriticalAreaRisk) {
    criticalArea.expanded = !criticalArea.expanded;

    if (criticalArea.expanded) {
      section.criticalAreas.forEach(ca => {
        if (ca !== criticalArea) {
          ca.expanded = false;
        }
      });
    }
  }

  logData(x: any) {
    console.log(x)
  }

  async onRegionSelect(region: ConsolidatedRegionRisk){
    if(this.isLoadingStructure){
      return;
    }

    let currentItem: ChangesModel = {
      assetId: this.assetId,
      regionId: region.id
    }

    this.isLoadingStructure = true
    await this.assetManagerLoaderService.verifyRegionAndSection(currentItem)
    this.isLoadingStructure = false

    this.assetManager.asset.mode = 'REGIONS'
    this.assetManagerLoaderService.highlightRegion(currentItem)
    this.assetManagerFilter.showAllCriticalAreas(false)

    //this.assetManager.updateViewMode('Risk')
    this.menuService.changeVisualizationModeAsync('Risk' as VisualizationMode, region.id);
  }

  async onSectionSelect(region: ConsolidatedRegionRisk, section: ConsolidatedSectionRisk){
    if(this.isLoadingStructure){
      return;
    }
    // this.currentSelectedSection = section

     let currentItem: ChangesModel = {
      assetId: this.assetId,
      regionId: region.id,
      sectionId: section.id
    }

    await this.assetManagerLoaderService.verifyRegionAndSection(currentItem)

    this.selectionService.setSectionSelectionFromListBox(this.assetManager.asset, region.id, section.id)
    this.assetManagerLoaderService.highlightSection(currentItem)
    this.menuService.changeVisualizationModeAsync('Risk' as VisualizationMode, region.id);
  }

  onCriticalAreaSelect(criticalArea: ConsolidatedCriticalAreaRisk) {
    this.assetManagerFilter.showAllCriticalAreas(true);
    this.assetManagerFilter.highlightCriticalAreas([criticalArea.id])
  }

  getConsequenceName(id: string): string {
    if(!this.riskMatrix?.consequenceItems) {
      return null
    }

    let consequence = this.riskMatrix.consequenceItems.find(x => x.id == id)

    if(!consequence) {
      return null
    }

    return consequence.name
  }

  getPofName(id: string): string {
    if(!this.riskMatrix?.probabilityOfFailureItems) {
      return null
    }

    let pof = this.riskMatrix.probabilityOfFailureItems.find(x => x.id == id)

    if(!pof) {
      return null
    }

    return pof.name
  }

  trackByFn(index, item) {
    // Assume each item has a unique id property
    return item.regionId;
  }

}
